import type { UtmKey, UtmValues } from '@/components/builder/UtmSection'
import type { UrlParam } from '@/lib/buildUrl'
import type { PickEmission } from '@/lib/pickerBindings'
import type { TagEntry } from '@/lib/tagEntry'

interface ComposeInputs {
  utm: UtmValues
  tags: ReadonlyArray<TagEntry>
  pickEmissions?: ReadonlyArray<PickEmission>
  /** Reserved, but left off the link: an emitted ID already names them. */
  impliedKeys?: ReadonlySet<string>
}

export interface ComposeResult {
  params: UrlParam[]
  /**
   * Keys claimed by UTM params or analytics tags. Excludes custom-row keys so a
   * custom parameter isn't reported as colliding with itself; sibling collisions
   * among custom rows are detected separately by the custom section.
   */
  reservedKeys: ReadonlySet<string>
}

export function composeParams({
  utm,
  tags,
  pickEmissions = [],
  impliedKeys = new Set<string>(),
}: ComposeInputs): ComposeResult {
  const params: UrlParam[] = []
  const reserved = new Set<string>()

  for (const key of Object.keys(utm) as UtmKey[]) {
    const value = utm[key] ?? ''
    const fullKey = `utm_${key}`
    reserved.add(fullKey)
    if (value === '') continue
    params.push([fullKey, value])
  }

  // Two passes so duplicate keys collide consistently with reservedKeys.
  const keyCounts = new Map<string, number>()
  for (const tag of tags) {
    if (tag.key === '') continue
    keyCounts.set(tag.key, (keyCounts.get(tag.key) ?? 0) + 1)
  }

  // Twin keys are reserved up front so a pick's emission cannot be shadowed;
  // derivePickEmissions has already dropped any emission whose twin key the
  // user claimed with a row of their own.
  const emissionByNameKey = new Map<string, PickEmission>()
  for (const emission of pickEmissions) {
    emissionByNameKey.set(emission.nameKey, emission)
    reserved.add(emission.idKey)
  }

  for (const tag of tags) {
    if (tag.key === '') continue
    if (reserved.has(tag.key)) continue
    if ((keyCounts.get(tag.key) ?? 0) > 1) continue
    reserved.add(tag.key)
    if (tag.value === '') continue
    if (impliedKeys.has(tag.key)) continue
    const emission = emissionByNameKey.get(tag.key)
    if (emission) {
      params.push([emission.idKey, emission.idValue])
    } else {
      params.push([tag.key, tag.value])
    }
  }

  // Drop custom-row keys: a row reserving its own key would self-flag as a duplicate.
  const customKeys = new Set<string>()
  for (const tag of tags) {
    if (tag.rawKey !== undefined && tag.key !== '') customKeys.add(tag.key)
  }
  const reservedKeys = new Set<string>()
  for (const key of reserved) {
    if (!customKeys.has(key)) reservedKeys.add(key)
  }

  return { params, reservedKeys }
}
